/**
 * Vehicle Booking Page — Pool-Fahrzeuge buchen mit Wochen-Kalender.
 * Jede Zeile = ein Pool-Fahrzeug. Spalten = die 7 Tage der gewählten Woche.
 * Klick in eine Zelle öffnet den Buchungs-Dialog.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Car, MapPin, ChevronLeft, ChevronRight, Loader2, X, Trash2, Plus, Pencil,
  Snowflake, Sun, Calendar as CalendarIcon, User as UserIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, parseISO, isSameDay, getISOWeek, addWeeks, subWeeks } from 'date-fns';
import { de } from 'date-fns/locale';
import { vehiclesAPI, branchesAPI, locationsAPI, employeesAPI, companyAPI } from '../lib/api';
import { OrgUnitFilter, orgUnitToLegacyParams } from '../components/OrgUnitFilter';
import { formatLastFirst } from '../lib/nameFormat';

const TIRE_LABELS = {
  summer: { label: 'Sommer', icon: Sun, color: 'text-amber-600' },
  winter: { label: 'Winter', icon: Snowflake, color: 'text-blue-600' },
  allseason: { label: 'Ganzjahr', icon: Sun, color: 'text-emerald-600' },
};

function bookingTimeRange(b) {
  return `${b.start_datetime.slice(11, 16)} – ${b.end_datetime.slice(11, 16)}`;
}

// Buchung erstreckt sich über den Tag dayStr (yyyy-MM-dd)?
const bookingSpansDay = (b, dayStr) =>
  b.start_datetime.slice(0, 10) <= dayStr && dayStr <= b.end_datetime.slice(0, 10);

// Zeit-/Tageslabel je nach Tag innerhalb einer (ggf. mehrtägigen) Buchung.
function bookingDayLabel(b, dayStr) {
  const sDay = b.start_datetime.slice(0, 10);
  const eDay = b.end_datetime.slice(0, 10);
  if (sDay === eDay) return bookingTimeRange(b);
  if (dayStr === sDay) return `ab ${b.start_datetime.slice(11, 16)}`;
  if (dayStr === eDay) return `bis ${b.end_datetime.slice(11, 16)}`;
  return 'ganztägig';
}

export default function VehicleBooking() {
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission('fleet', 'edit');

  const [poolVehicles, setPoolVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterOrgUnit, setFilterOrgUnit] = useState('all');
  const [orgTree, setOrgTree] = useState([]);
  const [orgLevels, setOrgLevels] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    vehicle_id: '', purpose: '', start_datetime: '', end_datetime: '',
    employee_id: '', expected_km: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const rangeStart = format(weekStart, "yyyy-MM-dd'T'00:00:00");
  const rangeEnd = format(addDays(weekStart, 6), 'yyyy-MM-dd');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.assign(params, orgUnitToLegacyParams(orgTree, filterOrgUnit));

      const [pv, bk, br, lo, emp] = await Promise.all([
        vehiclesAPI.poolVehicles(params),
        vehiclesAPI.listBookings({ start_date: rangeStart, end_date: rangeEnd }),
        branchesAPI.getAll(),
        locationsAPI.getAll(),
        employeesAPI.getAll(),
      ]);
      setPoolVehicles(pv.data);
      setBookings(bk.data);
      setBranches(br.data);
      setLocations(lo.data);
      setEmployees(emp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [filterOrgUnit, rangeStart, rangeEnd, orgTree]);

  useEffect(() => {
    companyAPI.getOrgTree()
      .then((t) => { setOrgTree(t.data?.tree || []); setOrgLevels(t.data?.levels || []); })
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-Auswahl des eigenen Mitarbeiters als Buchungsperson
  const myEmployee = useMemo(() => {
    if (!user) return null;
    return employees.find(e => e.user_id === user.id) || null;
  }, [employees, user]);
  const myEmployeeId = myEmployee?.id || '';
  const myLocationNames = useMemo(() => {
    if (!myEmployee) return [];
    const ids = [myEmployee.location_id, ...(myEmployee.additional_location_ids || [])].filter(Boolean);
    return ids
      .map((id) => locations.find((l) => l.id === id)?.name)
      .filter(Boolean);
  }, [myEmployee, locations]);

  const openBookingDialog = (vehicle, day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    setEditingId(null);
    setBookingForm({
      vehicle_id: vehicle.id,
      purpose: '',
      start_datetime: `${dateStr}T08:00`,
      end_datetime: `${dateStr}T17:00`,
      employee_id: myEmployeeId || '',
      expected_km: '',
      notes: '',
    });
    setDialogOpen(true);
  };

  const openEditDialog = (booking) => {
    setEditingId(booking.id);
    setBookingForm({
      vehicle_id: booking.vehicle_id,
      purpose: booking.purpose || '',
      start_datetime: booking.start_datetime.slice(0, 16),
      end_datetime: booking.end_datetime.slice(0, 16),
      employee_id: booking.employee_id || '',
      expected_km: booking.expected_km != null ? String(booking.expected_km) : '',
      notes: booking.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!bookingForm.purpose.trim()) {
      toast.error('Bitte einen Zweck angeben');
      return;
    }
    if (bookingForm.start_datetime >= bookingForm.end_datetime) {
      toast.error('Endzeit muss nach Startzeit liegen');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...bookingForm,
        start_datetime: `${bookingForm.start_datetime}:00`,
        end_datetime: `${bookingForm.end_datetime}:00`,
        expected_km: bookingForm.expected_km ? parseInt(bookingForm.expected_km, 10) : null,
        employee_id: bookingForm.employee_id || null,
        notes: bookingForm.notes || null,
      };
      if (editingId) {
        await vehiclesAPI.updateBooking(editingId, payload);
        toast.success('Buchung aktualisiert');
      } else {
        await vehiclesAPI.createBooking(payload);
        toast.success('Buchung erstellt');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Buchung nicht möglich');
    } finally {
      setSaving(false);
    }
  };

  const cancelBooking = async (booking) => {
    if (!window.confirm(`Buchung "${booking.purpose}" am ${booking.start_datetime.slice(0, 10)} stornieren?`)) return;
    try {
      await vehiclesAPI.cancelBooking(booking.id);
      toast.success('Buchung storniert');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Stornierung fehlgeschlagen');
    }
  };

  const bookingsByVehicle = useMemo(() => {
    const map = {};
    bookings.forEach(b => {
      (map[b.vehicle_id] = map[b.vehicle_id] || []).push(b);
    });
    return map;
  }, [bookings]);

  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const selectedVehicle = poolVehicles.find(v => v.id === bookingForm.vehicle_id);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in p-4 sm:p-6" data-testid="vehicle-booking-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground flex items-center gap-2">
              <Car className="h-7 w-7 text-primary" />
              Fahrzeugbuchung
            </h1>
            <p className="text-muted-foreground mt-1">
              {poolVehicles.length} Pool-Fahrzeug{poolVehicles.length === 1 ? '' : 'e'} verfügbar
              {myLocationNames.length > 0 && <span className="ml-1">• {myLocationNames.join(', ')}</span>}
            </p>
          </div>
        </div>

        {/* Standort-Info (analog Raumbuchung) */}
        {myEmployee && myLocationNames.length > 0 && (
          <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg border">
            <MapPin className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-medium">Ihr Standort: {myLocationNames.join(', ')}</p>
              <p className="text-xs text-muted-foreground">Sie sehen Pool-Fahrzeuge an Ihrem Standort oder Ihrer Niederlassung.</p>
            </div>
          </div>
        )}

        {myEmployee && myLocationNames.length === 0 && !canManage && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6 text-center">
              <MapPin className="h-10 w-10 mx-auto text-amber-500 mb-3" />
              <h3 className="font-medium text-lg">Kein Standort zugewiesen</h3>
              <p className="text-muted-foreground mt-1">
                Sie sind keinem Standort zugewiesen und können daher keine Pool-Fahrzeuge buchen.
                Bitte wenden Sie sich an Ihren Administrator.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Wochen-Navigation (analog Raumbuchung) */}
        <div className="flex items-center justify-between bg-card border rounded-lg p-4">
          <Button variant="ghost" size="icon" onClick={() => setWeekStart(subWeeks(weekStart, 1))} data-testid="btn-prev-week">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="text-center flex-1">
            <p className="font-semibold">
              KW {getISOWeek(weekStart)} • {format(weekStart, 'd. MMM', { locale: de })} – {format(addDays(weekStart, 6), 'd. MMM yyyy', { locale: de })}
            </p>
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
              data-testid="btn-today"
            >
              Heute
            </Button>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setWeekStart(addWeeks(weekStart, 1))} data-testid="btn-next-week">
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Filter (nur fuer Manager / Admin sichtbar) */}
        {canManage && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full sm:w-[240px]">
              <OrgUnitFilter
                tree={orgTree}
                levels={orgLevels}
                value={filterOrgUnit}
                onChange={setFilterOrgUnit}
                showLabel={false}
                testId="filter-org-unit"
              />
            </div>
          </div>
        )}

        {/* Wochen-Kalender */}
        <Card>
          <CardHeader>
            <CardTitle>Pool-Fahrzeuge ({poolVehicles.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>
            ) : poolVehicles.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Car className="h-12 w-12 mx-auto opacity-30 mb-3" />
                <p>Keine Pool-Fahrzeuge an Ihrem Standort verfügbar.</p>
                <p className="text-xs mt-2">Im Fuhrpark unter Verwendung „Poolfahrzeug" auswählen.</p>
              </div>
            ) : (
              <>
                {/* Desktop: Wochenkalender-Tabelle (>= md) */}
                <div className="hidden md:block overflow-x-auto">
                  <div className="min-w-[900px]">
                    {/* Header */}
                    <div className="grid grid-cols-[220px_repeat(7,1fr)] gap-2 mb-2">
                      <div className="text-xs font-medium text-muted-foreground p-2">Fahrzeug</div>
                      {days.map(d => {
                        const isToday = format(d, 'yyyy-MM-dd') === todayStr;
                        return (
                          <div key={d.toISOString()}
                            className={`text-center p-2 rounded text-xs font-medium ${isToday ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`}>
                            <div>{format(d, 'EEE', { locale: de })}</div>
                            <div className="text-base font-semibold mt-0.5">{format(d, 'd.M.')}</div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Rows */}
                    {poolVehicles.map(v => {
                      const tire = TIRE_LABELS[v.current_tires];
                      const TireIcon = tire?.icon;
                      const vBookings = bookingsByVehicle[v.id] || [];
                      return (
                        <div key={v.id} className="grid grid-cols-[220px_repeat(7,1fr)] gap-2 mb-2 items-stretch">
                          <div className="border rounded-lg p-3 bg-secondary/30">
                            <p className="font-mono font-semibold text-sm">{v.license_plate}</p>
                            <p className="text-xs text-muted-foreground">{v.brand} {v.model}</p>
                            {v.location_name && (
                              <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1">
                                <MapPin className="h-3 w-3" />
                                {v.location_name}
                              </p>
                            )}
                            {tire && (
                              <p className={`text-[11px] flex items-center gap-1 mt-0.5 ${tire.color}`}>
                                <TireIcon className="h-3 w-3" />
                                {tire.label}
                              </p>
                            )}
                          </div>
                          {days.map(d => {
                            const dayStr = format(d, 'yyyy-MM-dd');
                            const dayBookings = vBookings.filter(b => bookingSpansDay(b, dayStr));
                            return (
                              <div key={d.toISOString()}
                                onClick={() => openBookingDialog(v, d)}
                                data-testid={`cell-${v.id}-${dayStr}`}
                                className="border rounded-lg p-1 min-h-[80px] bg-card hover:bg-secondary/40 cursor-pointer transition-colors flex flex-col gap-1">
                                {dayBookings.length === 0 ? (
                                  <div className="flex items-center justify-center h-full opacity-0 hover:opacity-60 transition-opacity">
                                    <Plus className="h-4 w-4" />
                                  </div>
                                ) : (
                                  dayBookings.map(b => {
                                    const isMine = b.created_by === user?.id;
                                    const isMulti = b.start_datetime.slice(0, 10) !== b.end_datetime.slice(0, 10);
                                    const canEdit = isMine || canManage;
                                    return (
                                      <div key={b.id}
                                        onClick={(e) => { e.stopPropagation(); if (canEdit) openEditDialog(b); }}
                                        title={canEdit ? 'Zum Bearbeiten klicken' : ''}
                                        className={`text-[11px] p-1.5 rounded ${canEdit ? 'cursor-pointer' : 'cursor-default'} ${isMine ? 'bg-primary/15 border border-primary/40' : 'bg-amber-100 dark:bg-amber-900/30 border border-amber-300/50'}`}>
                                        <div className="font-medium truncate flex items-center justify-between gap-1">
                                          <span className="truncate">{b.purpose}</span>
                                          {canEdit && (
                                            <span className="flex items-center gap-1 flex-shrink-0">
                                              <button onClick={(e) => { e.stopPropagation(); openEditDialog(b); }} className="hover:text-primary" title="Bearbeiten" data-testid={`btn-edit-${b.id}`}>
                                                <Pencil className="h-3 w-3" />
                                              </button>
                                              <button onClick={(e) => { e.stopPropagation(); cancelBooking(b); }} className="hover:text-red-600" title="Stornieren" data-testid={`btn-cancel-${b.id}`}>
                                                <X className="h-3 w-3" />
                                              </button>
                                            </span>
                                          )}
                                        </div>
                                        <div className="opacity-70 truncate flex items-center gap-1">
                                          {isMulti && <span className="text-primary" title="mehrtägig">↔</span>}
                                          {bookingDayLabel(b, dayStr)}
                                        </div>
                                        {b.employee_name && <div className="opacity-70 truncate">{b.employee_name}</div>}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Mobile: Karten-Liste pro Fahrzeug + Tages-Pills (< md) */}
                <div className="md:hidden space-y-3">
                  {poolVehicles.map((v) => {
                    const tire = TIRE_LABELS[v.current_tires];
                    const TireIcon = tire?.icon;
                    const vBookings = bookingsByVehicle[v.id] || [];
                    return (
                      <div key={v.id} className="border rounded-lg overflow-hidden" data-testid={`vehicle-mobile-${v.id}`}>
                        {/* Vehicle Header */}
                        <div className="p-3 bg-secondary/40 border-b">
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-mono font-bold">{v.license_plate}</p>
                              <p className="text-sm text-muted-foreground truncate">{v.brand} {v.model}</p>
                            </div>
                            {tire && (
                              <span className={`text-[11px] flex items-center gap-1 shrink-0 ${tire.color}`}>
                                <TireIcon className="h-3 w-3" />
                                {tire.label}
                              </span>
                            )}
                          </div>
                          {v.location_name && (
                            <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {v.location_name}
                            </p>
                          )}
                        </div>
                        {/* Tages-Pills */}
                        <div className="grid grid-cols-7 gap-1 p-2 border-b">
                          {days.map((d) => {
                            const dayStr = format(d, 'yyyy-MM-dd');
                            const isToday = dayStr === todayStr;
                            const count = vBookings.filter((b) => bookingSpansDay(b, dayStr)).length;
                            return (
                              <button
                                key={d.toISOString()}
                                type="button"
                                onClick={() => openBookingDialog(v, d)}
                                data-testid={`mobile-day-${v.id}-${dayStr}`}
                                className={`flex flex-col items-center py-1.5 rounded-md text-[11px] transition-colors ${
                                  isToday ? 'bg-primary/10 text-primary' : 'bg-card hover:bg-secondary/60'
                                } ${count > 0 ? 'border border-primary/40' : 'border border-transparent'}`}
                              >
                                <span>{format(d, 'EE', { locale: de })}</span>
                                <span className="text-sm font-semibold">{format(d, 'd.M.')}</span>
                                {count > 0 ? (
                                  <span className="mt-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold">{count}</span>
                                ) : (
                                  <span className="mt-0.5 h-4" aria-hidden="true" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {/* Buchungs-Liste der Woche */}
                        {vBookings.length === 0 ? (
                          <p className="text-xs text-muted-foreground p-3 text-center">Keine Buchungen in dieser Woche</p>
                        ) : (
                          <ul className="divide-y">
                            {vBookings
                              .slice()
                              .sort((a, b) => a.start_datetime.localeCompare(b.start_datetime))
                              .map((b) => {
                                const isMine = b.created_by === user?.id;
                                const sDay = b.start_datetime.slice(0, 10);
                                const eDay = b.end_datetime.slice(0, 10);
                                const isMulti = sDay !== eDay;
                                const canEdit = isMine || canManage;
                                return (
                                  <li key={b.id} className={`p-3 ${isMine ? 'bg-primary/5' : ''}`}>
                                    <div className="flex justify-between items-start gap-2">
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium truncate">{b.purpose}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {isMulti ? (
                                            <span className="inline-flex items-center gap-1">
                                              <span className="text-primary">↔</span>
                                              {format(parseISO(b.start_datetime), 'EE d.M. HH:mm', { locale: de })} – {format(parseISO(b.end_datetime), 'EE d.M. HH:mm', { locale: de })}
                                            </span>
                                          ) : (
                                            <>{format(parseISO(b.start_datetime), 'EE d.M.', { locale: de })} · {bookingTimeRange(b)}</>
                                          )}
                                        </p>
                                        {b.employee_name && (
                                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                            <UserIcon className="h-3 w-3" />
                                            {b.employee_name}
                                          </p>
                                        )}
                                      </div>
                                      {canEdit && (
                                        <div className="flex items-center gap-1 shrink-0">
                                          <button
                                            onClick={() => openEditDialog(b)}
                                            className="text-muted-foreground hover:text-primary p-1"
                                            title="Bearbeiten"
                                            data-testid={`mobile-btn-edit-${b.id}`}
                                          >
                                            <Pencil className="h-4 w-4" />
                                          </button>
                                          <button
                                            onClick={() => cancelBooking(b)}
                                            className="text-muted-foreground hover:text-red-600 p-1"
                                            title="Stornieren"
                                            data-testid={`mobile-btn-cancel-${b.id}`}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </li>
                                );
                              })}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Buchungs-Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                {editingId ? 'Buchung bearbeiten' : 'Fahrzeug buchen'}
              </DialogTitle>
              {selectedVehicle && (
                <DialogDescription>
                  {selectedVehicle.license_plate} · {selectedVehicle.brand} {selectedVehicle.model}
                  {selectedVehicle.location_name && ` · ${selectedVehicle.location_name}`}
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Zweck *</Label>
                <Input
                  value={bookingForm.purpose}
                  onChange={e => setBookingForm({ ...bookingForm, purpose: e.target.value })}
                  placeholder="z.B. Kundentermin Erfurt"
                  data-testid="booking-purpose"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Von *</Label>
                  <Input
                    type="datetime-local"
                    value={bookingForm.start_datetime}
                    onChange={e => setBookingForm({ ...bookingForm, start_datetime: e.target.value })}
                    data-testid="booking-start"
                  />
                </div>
                <div>
                  <Label className="text-xs">Bis *</Label>
                  <Input
                    type="datetime-local"
                    value={bookingForm.end_datetime}
                    onChange={e => setBookingForm({ ...bookingForm, end_datetime: e.target.value })}
                    data-testid="booking-end"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><UserIcon className="h-3 w-3" />Fahrer/in</Label>
                <Select value={bookingForm.employee_id || 'self'} onValueChange={v => setBookingForm({ ...bookingForm, employee_id: v === 'self' ? '' : v })}>
                  <SelectTrigger data-testid="booking-employee"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">— ich selbst —</SelectItem>
                    {employees.filter(e => e.is_active !== false).map(e => (
                      <SelectItem key={e.id} value={e.id}>{formatLastFirst(e)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Geschätzte Kilometer (optional)</Label>
                <Input
                  type="number"
                  value={bookingForm.expected_km}
                  onChange={e => setBookingForm({ ...bookingForm, expected_km: e.target.value })}
                  placeholder="z.B. 250"
                />
              </div>
              <div>
                <Label className="text-xs">Notizen</Label>
                <Textarea
                  value={bookingForm.notes}
                  onChange={e => setBookingForm({ ...bookingForm, notes: e.target.value })}
                  rows={2}
                  placeholder="Optional: Hinweise zur Fahrt..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Abbrechen</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="btn-save-booking">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarIcon className="h-4 w-4 mr-2" />}
                {editingId ? 'Speichern' : 'Buchen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Legende */}
        {!loading && poolVehicles.length > 0 && (
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            <Badge className="bg-primary/15 text-primary border-primary/40">Eigene Buchung</Badge>
            <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">Andere Buchung</Badge>
            <span>Klick in eine leere Zelle erstellt eine neue Buchung.</span>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
